import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Trash2, Loader2, Star, TrendingDown, Handshake } from 'lucide-react';
import { tsr } from '@flote/app-core';
import { toast } from 'sonner';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Button,
  Separator,
  QuantityCounter,
  Badge,
} from '@flote/ui';
import { useCart, useCartTotals, useCartDrawer, useCartLoading, getPriceDisplayInfo, useFunnelTracking, isCartItemBackordered, hasBackorderedItems } from '@flote/app-core';
import type { CartItem } from '@flote/app-core';
import { AlertTriangle } from 'lucide-react';

export function CartDrawer() {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, syncCartItems } = useCart();
  const { totalPrice, totalItems: itemCount } = useCartTotals();
  const { isOpen: cartOpen, close: closeCart } = useCartDrawer();
  const { isRemovingItem, isUpdatingItem } = useCartLoading();
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
  const { trackCartViewed, trackProductRemovedFromCart, trackCartQuantityUpdated } = useFunnelTracking();
  const hasTrackedOpen = useRef(false);
  const hasSyncedCart = useRef(false);

  // Get product IDs from cart items for sync query
  const productIds = useMemo(() => items.map(item => item.productId), [items]);

  // Fetch fresh product data when cart opens to sync statusId/canBackorder
  const { data: productsData } = tsr.products.b2bwaveListByIds.useQuery({
    queryKey: ['cart-products-sync', productIds],
    queryData: { query: { ids: productIds.join(',') } },
    enabled: cartOpen && productIds.length > 0 && !hasSyncedCart.current,
    staleTime: 30 * 1000, // 30 seconds
  });

  // Sync cart items with fresh product data
  useEffect(() => {
    if (productsData?.status === 200 && productsData.body.length > 0 && !hasSyncedCart.current) {
      syncCartItems(productsData.body);
      hasSyncedCart.current = true;
    }
  }, [productsData, syncCartItems]);

  // Reset sync flag when cart closes
  useEffect(() => {
    if (!cartOpen) {
      hasSyncedCart.current = false;
    }
  }, [cartOpen]);

  // Track cart viewed when drawer opens
  useEffect(() => {
    if (cartOpen && !hasTrackedOpen.current) {
      trackCartViewed({ itemCount, cartTotal: totalPrice });
      hasTrackedOpen.current = true;
    }
    if (!cartOpen) {
      hasTrackedOpen.current = false;
    }
  }, [cartOpen, itemCount, totalPrice, trackCartViewed]);

  const handleProceedToCheckout = async () => {
    setIsProcessingCheckout(true);
    try {
      closeCart();
      navigate('/checkout', {
        state: {
          bypassCheck: true,
          cartItems: items,
        },
        replace: false,
      });
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to process checkout', {
        description: 'Please try again later',
      });
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    try {
      await removeItem(itemId);
      if (item) {
        trackProductRemovedFromCart({
          productId: item.productId,
          productName: item.name,
          quantity: item.quantity,
        });
      }
      toast.success('Item removed from cart');
    } catch (error) {
      console.error('Remove item error:', error);
      toast.error('Failed to remove item', {
        description: 'Please try again later',
      });
    }
  };

  const handleUpdateQuantity = async (itemId: string, newQuantity: number) => {
    const item = items.find(i => i.id === itemId);
    const previousQuantity = item?.quantity ?? 0;
    try {
      await updateQuantity(itemId, newQuantity);
      if (item) {
        const priceInfo = getPriceDisplayInfo(item);
        trackCartQuantityUpdated({
          productId: item.productId,
          productName: item.name,
          previousQuantity,
          newQuantity,
          price: priceInfo.effectivePrice,
        });
      }
      toast.success('Cart updated');
    } catch (error) {
      console.error('Update quantity error:', error);
      toast.error('Failed to update quantity', {
        description: 'Please try again later',
      });
    }
  };

  return (
    <Sheet open={cartOpen} onOpenChange={(open) => !open && closeCart()}>
      <SheetContent className="flex w-[90vw] flex-col overflow-hidden p-0 sm:max-w-md">
        <SheetHeader className="flex-shrink-0 border-b px-6 py-4">
          <div className="mb-3 flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <ShoppingBag className="h-5 w-5" />
              <span>Your Cart {itemCount > 0 && `(${itemCount})`}</span>
            </SheetTitle>
          </div>
          {items.length > 0 && (
            <Button variant="outline" size="sm" onClick={closeCart} className="w-full">
              Continue Shopping
            </Button>
          )}
        </SheetHeader>

        {items.length > 0 && (
          <>
            <div className="min-h-0 flex-1 overflow-auto px-6 py-2">
              <div className="space-y-4 py-2">
                {items.map((item: CartItem) => {
                  const priceInfo = getPriceDisplayInfo(item);
                  const itemTotalPrice = priceInfo.effectivePrice * item.quantity;
                  const isBackordered = isCartItemBackordered(item);

                  return (
                    <div key={item.id} className="animate-in fade-in">
                      <div className="flex gap-4">
                        <div className="h-20 w-20 overflow-hidden rounded-md bg-secondary/30">
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="h-full w-full object-cover"
                          />
                        </div>

                        <div className="flex flex-1 flex-col justify-between">
                          <div>
                            <h4 className="line-clamp-1 text-sm font-medium">{item.name}</h4>
                            <div className="mt-1 flex items-center gap-2 flex-wrap">
                              <p className="text-xs text-muted-foreground">{item.type}</p>
                              {isBackordered && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0.5 bg-amber-50 text-amber-700 border-amber-200 flex items-center gap-0.5">
                                  <AlertTriangle className="h-2.5 w-2.5" />
                                  <span>Backordered</span>
                                </Badge>
                              )}
                              {priceInfo.hasCustomerPricing && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0.5 bg-green-50 text-green-700 border-green-200 flex items-center gap-0.5">
                                  <Star className="h-2.5 w-2.5 fill-current" />
                                  <span>Your Price</span>
                                </Badge>
                              )}
                              {priceInfo.hasBulkPricing && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-700 border-blue-200 flex items-center gap-0.5">
                                  <TrendingDown className="h-2.5 w-2.5" />
                                  <span>Bulk</span>
                                </Badge>
                              )}
                              {priceInfo.hasBuyerPricing && (
                                <Badge variant="secondary" className="text-[10px] px-1 py-0.5 bg-flote-red-50 text-flote-red-700 border-flote-red-200 flex items-center gap-0.5">
                                  <Handshake className="h-2.5 w-2.5" />
                                  <span>Org Price</span>
                                </Badge>
                              )}
                            </div>
                            {priceInfo.priceType !== 'standard' && priceInfo.originalPrice && (
                              <p className="text-xs text-muted-foreground line-through">
                                ${priceInfo.originalPrice.toFixed(2)} each
                              </p>
                            )}
                            {priceInfo.priceType !== 'standard' && (
                              <p className="text-xs font-medium text-green-600">
                                ${priceInfo.effectivePrice.toFixed(2)} each
                              </p>
                            )}
                          </div>

                          <div className="mt-2 flex items-center justify-between">
                            <QuantityCounter
                              value={item.quantity}
                              onChange={(newQty) => handleUpdateQuantity(item.id, newQty)}
                              min={1}
                              max={item.inventory ?? 20} // Prevent overselling by reserving 10 units
                              disabled={isUpdatingItem(item.id)}
                            />
                            {isUpdatingItem(item.id) && (
                              <Loader2 className="ml-2 h-4 w-4 animate-spin text-muted-foreground" />
                            )}
                          </div>
                        </div>

                        <div className="flex flex-col items-end justify-between">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleRemoveItem(item.id)}
                            disabled={isRemovingItem(item.id)}
                          >
                            {isRemovingItem(item.id) ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-muted-foreground" />
                            )}
                            <span className="sr-only">Remove</span>
                          </Button>

                          <div className="text-right">
                            <span className="block text-base font-medium">
                              ${itemTotalPrice.toFixed(2)}
                            </span>
                            {priceInfo.priceType !== 'standard' && priceInfo.savings && (
                              <span className="text-xs font-medium text-green-600">
                                Save ${(priceInfo.savings * item.quantity).toFixed(2)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <Separator className="mb-4 mt-4" />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex-shrink-0 space-y-4 border-t p-6">
              {hasBackorderedItems(items) && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 text-amber-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-amber-800">Backordered items in cart</p>
                      <p className="text-amber-700 mt-0.5">
                        Please remove backordered items before checkout.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Subtotal</span>
                  <span className="text-sm">${totalPrice.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Shipping</span>
                  <span className="text-sm">Free</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Taxes</span>
                  <span className="text-sm">Calculated at checkout</span>
                </div>
                <Separator className="my-6 py-0" />
                <div className="flex items-center justify-between font-medium">
                  <span>Total</span>
                  <span>${totalPrice.toFixed(2)}</span>
                </div>
              </div>

              <Button
                size="lg"
                onClick={handleProceedToCheckout}
                disabled={isProcessingCheckout || hasBackorderedItems(items)}
                className="w-full"
              >
                {isProcessingCheckout ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Proceed to Checkout'
                )}
              </Button>
            </div>
          </>
        )}

        {items.length === 0 && (
          <div className="flex flex-1 flex-col items-center justify-center p-6">
            <ShoppingBag className="mb-4 h-12 w-12 text-muted-foreground" />
            <h3 className="mb-1 text-lg font-medium">Your cart is empty</h3>
            <p className="mb-6 text-center text-muted-foreground">
              Add items to your cart to continue shopping
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
